import { Fragment, useState } from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import {
  LuChevronDown,
  LuChevronUp,
  LuChevronLeft,
  LuChevronRight,
  LuChevronsLeft,
  LuChevronsRight,
} from 'react-icons/lu';
import { ImDatabase } from 'react-icons/im';
import type { AgentApiAnalysisSalesDetails } from '@/types/serializers';
import InsightEmptyState from '../InsightEmptyState';
import SalesRegisterButton from './SalesRegisterButton';
import { formatYen } from './salesChartUtils';

interface Props {
  details: AgentApiAnalysisSalesDetails;
  onPageChange: (page: number) => void;
}

/**
 * 売上明細テーブル。月別行をクリックで展開し、日次明細（日付/求職者/企業/売上金額）を表示する。
 * 月行単位でページネーションする。
 */
export default function SalesDetailTable({ details, onPageChange }: Props) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const isEmpty = details.months.length === 0;
  const currentPage = details.current_page;

  const toggle = (period: string) => {
    setExpanded((prev) => ({ ...prev, [period]: !prev[period] }));
  };

  return (
    <div className="overflow-hidden rounded-sm border border-solid border-border bg-white">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-solid border-border bg-gray">
              <th className="w-[180px] px-md py-sm text-left align-middle text-heading-xxs text-sub">
                日付
              </th>
              <th className="px-md py-sm text-center align-middle text-heading-xxs text-sub">
                求職者
              </th>
              <th className="px-md py-sm text-center align-middle text-heading-xxs text-sub">
                企業
              </th>
              <th className="px-md py-sm text-center align-middle text-heading-xxs text-sub">
                売上金額
              </th>
              <th className="w-[120px] px-md py-sm text-center align-middle text-heading-xxs text-sub">
                合計件数
              </th>
            </tr>
          </thead>
          <tbody>
            {isEmpty && (
              <tr>
                <td colSpan={5} className="p-0">
                  <div className="relative">
                    <div className="absolute top-md right-md">
                      <SalesRegisterButton />
                    </div>
                    <InsightEmptyState
                      icon={ImDatabase}
                      description="データを登録すると、ここにグラフが表示されます"
                    />
                  </div>
                </td>
              </tr>
            )}
            {details.months.map((month) => {
              const isOpen = expanded[month.period] ?? false;
              return (
                <Fragment key={month.period}>
                  <tr
                    className="h-10 cursor-pointer border-b border-solid border-border hover:bg-gray-50"
                    onClick={() => toggle(month.period)}
                  >
                    <td className="px-md py-2 align-middle text-body-sm text-main">
                      <div className="flex items-center gap-xs">
                        {month.period.replace('-', '/')}
                        {isOpen ? (
                          <LuChevronUp className="size-4 text-sub" />
                        ) : (
                          <LuChevronDown className="size-4 text-sub" />
                        )}
                      </div>
                    </td>
                    <td className="px-md py-2 text-center align-middle text-body-sm text-sub">―</td>
                    <td className="px-md py-2 text-center align-middle text-body-sm text-sub">―</td>
                    <td className="px-md py-2 text-center align-middle text-body-sm text-main">
                      {formatYen(month.total_value)}
                    </td>
                    <td className="px-md py-2 text-center align-middle text-body-sm text-main">
                      {month.count}
                    </td>
                  </tr>
                  {isOpen &&
                    month.rows.map((row, idx) => (
                      <tr
                        key={`${month.period}-${idx}`}
                        className="h-10 border-b border-solid border-border"
                      >
                        <td className="px-md py-2 align-middle text-body-sm text-sub">
                          {row.date.replace(/-/g, '/')}
                        </td>
                        <td className="px-md py-2 text-center align-middle text-body-sm text-sub">
                          {row.candidate_name ?? '―'}
                        </td>
                        <td className="px-md py-2 text-center align-middle text-body-sm text-sub">
                          {row.company_name ?? '―'}
                        </td>
                        <td className="px-md py-2 text-center align-middle text-body-sm text-sub">
                          {formatYen(row.value)}
                        </td>
                        <td className="px-md py-2 text-center align-middle text-body-sm text-sub">
                          ―
                        </td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {!isEmpty && (
        <div className="flex items-center justify-end gap-2 px-md py-sm">
          <p className="m-0 flex items-baseline gap-1 text-main">
            <span className="text-lg">{details.total_count.toLocaleString()}</span>
            <span className="text-sm">件中</span>
            <span className="text-lg">{details.months.length.toLocaleString()}</span>
            <span className="text-sm">件</span>
          </p>
          <PageIconButton
            ariaLabel="最初のページへ"
            disabled={details.is_first_page}
            onClick={() => onPageChange(1)}
          >
            <LuChevronsLeft className="size-5" />
          </PageIconButton>
          <PageIconButton
            ariaLabel="前のページへ"
            disabled={details.is_first_page}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <LuChevronLeft className="size-5" />
          </PageIconButton>
          {Array.from({ length: Math.min(details.total_pages, 3) }, (_, i) => {
            const startPage = Math.max(1, Math.min(currentPage - 1, details.total_pages - 2));
            const page = startPage + i;
            const isActive = page === currentPage;
            return (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                aria-current={isActive ? 'page' : undefined}
                className={clsx(
                  'flex size-[42px] items-center justify-center rounded text-sm font-bold',
                  isActive
                    ? 'bg-primary text-white'
                    : 'border border-solid border-border bg-white text-main hover:bg-gray-50'
                )}
              >
                {page}
              </button>
            );
          })}
          <PageIconButton
            ariaLabel="次のページへ"
            disabled={details.is_last_page}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <LuChevronRight className="size-5" />
          </PageIconButton>
          <PageIconButton
            ariaLabel="最後のページへ"
            disabled={details.is_last_page}
            onClick={() => onPageChange(details.total_pages)}
          >
            <LuChevronsRight className="size-5" />
          </PageIconButton>
        </div>
      )}
    </div>
  );
}

/** ページネーション用のアイコンボタン（最初/前/次/最後）。AnalysisDailyTable と同一デザイン。 */
function PageIconButton({
  children,
  onClick,
  disabled,
  ariaLabel,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="flex size-[42px] items-center justify-center rounded border border-solid border-border bg-white text-sub hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}
